import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO } from '../components/SEO';
import { useLocation } from 'wouter';
import { ArrowRight, Shield, MapPin, Layers, ChevronRight, Grid3x3 as Grid3X3 } from 'lucide-react';
import { cities } from '../data/cities';
import Logo from '../components/Logo';
import { Button } from '../components/ui/Button';
import { SupportFooter } from '../components/SupportFooter';

export function DirectoryIndexPage() {
  const [_, navigate] = useLocation();

  const seoTitle = 'Software Tool Directory | btoolme';
  const seoDescription = 'Browse 100+ business software tools across 36 categories. Find the best accounting, CRM, marketing, project management, and e-commerce tools for startups and small businesses.';
  const seoKeywords = 'business software directory, small business tools, software comparison, best business apps, startup tools, accounting software, CRM tools, marketing software, project management, e-commerce tools';

  const directorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Business Software Tool Directory",
    "description": seoDescription,
    "url": "https://btoolme.com/directory",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://btoolme.com" },
        { "@type": "ListItem", "position": 2, "name": "Directory", "item": "https://btoolme.com/directory" }
      ]
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" id="main-content">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path="/directory"
        keywords={seoKeywords}
        schema={directorySchema}
        breadcrumbs={[{ name: 'Tool Directory', path: '/directory' }]}
      />

      {/* Breadcrumbs */}
      <nav className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <a href="/" className="text-gray-500 hover:text-gray-700">Home</a>
            </li>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <li className="text-gray-900 font-medium">Directory</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-white">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-100/40 rounded-full blur-3xl" />
          <div className="absolute top-20 -left-20 w-60 h-60 bg-cyan-100/30 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center max-w-3xl mx-auto">
            <a href="/" className="inline-block mb-6">
              <Logo />
            </a>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight mb-5">
              Software Tool Directory
            </h1>

            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Browse our curated collection of business tools, or get personalized recommendations based on your specific needs.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => navigate('/questionnaire')}
                className="group px-8 py-4 text-lg w-full sm:w-auto"
                size="lg"
              >
                Get My Personalized Blueprint
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>

              <a
                href="/tools"
                className="group flex items-center gap-2 px-6 py-4 text-gray-700 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all font-medium w-full sm:w-auto justify-center"
              >
                <Grid3X3 className="w-5 h-5 text-gray-500 group-hover:text-blue-600 transition-colors" />
                Browse All Tools
              </a>
            </div>

            <p className="mt-4 text-sm text-gray-500">
              Tool popularity varies by business type — take the quiz for recommendations tailored to you.
            </p>
          </div>
        </div>
      </section>

      {/* Best 100 Tools Section */}
      <section className="py-12 sm:py-16 bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Layers className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Best 100 Tools for Small Business
              </h2>
              <p className="text-gray-600 mt-1">
                Browse our curated directory of tools across all categories.
              </p>
            </div>
          </div>

          <a
            href="/tools"
            className="group block bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                  Explore All Tools
                </h3>
                <p className="text-gray-600 mt-1">
                  Search, filter, and compare tools for scheduling, invoicing, CRM, marketing, and more.
                </p>
              </div>
              <ArrowRight className="w-6 h-6 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
            </div>
          </a>
        </div>
      </section>

      {/* Browse by City Section */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <MapPin className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Browse by City
              </h2>
              <p className="text-gray-600 mt-1">
                Find software recommendations with local business context.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cities.map((city) => (
              <a
                key={city.slug}
                href={`/directory/${city.slug}`}
                className="group bg-white p-5 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {city.name}
                    </h3>
                    <p className="text-sm text-gray-500">{city.state}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 sm:p-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Not sure where to start?
            </h2>
            <p className="text-gray-300 mb-8 max-w-xl mx-auto">
              Answer a few questions about your business and get a personalized software blueprint — not just a generic list.
            </p>

            <div className="flex flex-col items-center gap-4">
              <Button
                onClick={() => navigate('/questionnaire')}
                className="group px-8 py-4 text-lg bg-white text-gray-900 hover:bg-gray-100 font-semibold rounded-xl"
                size="lg"
              >
                Build My Blueprint
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>

              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-medium text-white">
                  100% Confidence Guaranteed
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SupportFooter />
    </div>
  );
}
